import { Box, Flex, LabelledCheckbox, ModalContent, Text } from '@modusign/design-system';
import { getLabelList } from '@modusign/openapi-client-category';
import { useAttachments, useDocument, useVerification } from '@modusign/react-signing';
import { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import noop from '@libs/noop';

import Table from '@app/components/Table';
import DocumentLabelSelectionContent from '@app/containers/Modal/EditorContractSetupPreviewModal/DocumentLabelSelectionContent';
import {
  getLabels,
  getMaxSigningPerParticipant,
} from '@app/features/contractLink/contractLinkCreate';
import { contractLinkCreateSetLabels } from '@app/features/contractLink/contractLinkCreate/actions';
import { useWorkspaceId } from '@app/hooks/useWorkspaceId';

import ContractLinkAgreementCheck from '../../../types/ContractLinkAgreementCheck';
import ContractLinkAgreementCheckKey from '../../../types/ContractLinkAgreementCheckKey';
import ContractLinkParticipantSettingInfo from './ContractLinkParticipantSettingInfo';

interface ContractLinkCreateInfoDesktopContentProps {
  signerId: string;
  agreements: ContractLinkAgreementCheck;
  onCheckChange: (check: { name: ContractLinkAgreementCheckKey; value: boolean }) => void;
}

function ContractLinkCreateInfoDesktopContent({
  signerId,
  agreements,
  onCheckChange,
}: ContractLinkCreateInfoDesktopContentProps) {
  const workspaceId = useWorkspaceId();
  const dispatch = useDispatch();

  const { document } = useDocument();
  const maxSigningPerParticipant = useSelector(getMaxSigningPerParticipant);
  const { verification } = useVerification(signerId);
  const { attachments } = useAttachments();

  const requesterAttachments = useMemo(
    () => attachments.filter((attachment) => attachment.participantReference.type === 'REQUESTER'),
    [attachments]
  );
  const isMobileAuthRequired = verification?.methods.some(
    (method) => method.type === 'MOBILE_IDENTIFICATION'
  );
  const labels = useSelector(getLabels);

  const [isLabelPopoverOpen, setLabelPopoverOpen] = useState(false);

  const [titles, values] = useMemo(() => {
    const titles = ['링크 이름', '서명자'];
    const values: Array<JSX.Element | string | undefined | null> = [
      <Text as="p" key="key-1" m={0} p={0} px={1} textAlign="left">
        {document.title}
      </Text>,
      <Flex justifyContent="space-between" key="key-2" px={2} role="table">
        <ContractLinkParticipantSettingInfo name="중복 서명 허용 (1인 최대 서명 건수)">
          {maxSigningPerParticipant ? (
            `${maxSigningPerParticipant}건`
          ) : (
            <Text color="textNeutralVeryLight">허용 안함</Text>
          )}
        </ContractLinkParticipantSettingInfo>
        <ContractLinkParticipantSettingInfo name="인증 수단" width="120px">
          <Text color={isMobileAuthRequired ? 'textNeutralBase' : 'textNeutralVeryLight'}>
            {isMobileAuthRequired ? '휴대폰 본인 인증' : '설정 안함'}
          </Text>
        </ContractLinkParticipantSettingInfo>
      </Flex>,
    ];

    if (requesterAttachments.length > 0) {
      titles.push('첨부한 파일');
      values.push(
        <Text as="p" m={0} p={0} px={1} textAlign="left">
          {requesterAttachments
            .map((a) => (a.file?.name ?? '') + (a.file?.extension ?? ''))
            .join(', ')}
        </Text>
      );
    }

    return [titles, values];
  }, [document.title, maxSigningPerParticipant, isMobileAuthRequired, requesterAttachments]);

  return (
    <ModalContent mb={0} px={9}>
      <Table
        direction="vertical"
        headClassName="EditorContractSetupPreviewModal__table-head"
        titles={titles}
        values={[values]}
      />
      <Box borderBottom="1px solid" borderBottomColor="dividerNeutralBase" mt={4} pb={7}>
        <DocumentLabelSelectionContent
          isPopoverOpen={isLabelPopoverOpen}
          selectedLabels={labels}
          setIsPopoverOpen={setLabelPopoverOpen}
          setSelectedLabelIds={async (labelIds) => {
            const { labels } = labelIds?.length
              ? await getLabelList(workspaceId, { labelIds })
              : { labels: [] };
            dispatch(contractLinkCreateSetLabels(labels));
          }}
        />
      </Box>
      <Box mb={0} mt={0} px={[0, 8, 10]} py={7} textAlign="left" width="100%">
        <LabelledCheckbox
          checked={agreements.linkParticipantSign}
          id="agreement-link-participant-sign"
          variant="medium"
          onChange={noop}
          onClick={() =>
            onCheckChange({ name: 'linkParticipantSign', value: !agreements.linkParticipantSign })
          }
        >
          <Text color="textNeutralSlightlyLight" fontWeight="medium">
            링크로 접속한 서명자가 문서 내용을 조회하고 서명 참여할 수 있음을 동의합니다.
          </Text>
        </LabelledCheckbox>
        <LabelledCheckbox
          checked={agreements.linkUsageDeduct}
          id="agreement-link-usage-deduct"
          variant="medium"
          onChange={noop}
          onClick={() =>
            onCheckChange({ name: 'linkUsageDeduct', value: !agreements.linkUsageDeduct })
          }
        >
          <Text color="textNeutralSlightlyLight" fontWeight="medium">
            서명자의 서명이 완료되면 사용량이 차감됩니다.
          </Text>
        </LabelledCheckbox>
      </Box>
    </ModalContent>
  );
}

export default ContractLinkCreateInfoDesktopContent;
